/**
 * L6 elites (ตัวหัวหน้า): at setup 2 % of the region's table monsters become elites: tagged `ELITE.tag` (the title,
 * drawn by the client as a red pool underfoot), x3 hp, x1.5 damage; killing one pays extra silver and qi (to the pool).
 * Rolls use the injected rng, in entity order. Bosses and arena foes are never elites.
 */
import { ELITE, ELITE_TABLE } from '../data/bounties';
import { regionOf } from '../data/regions';
import { getMonster } from '../data/monsters';
import type { ZoneId } from '../data/zones';
import type { Entity, WorldEvent } from '../world';
import type { SystemHost, WorldSystem } from './types';

export class ElitesSystem implements WorldSystem {
  readonly name = 'elites';
  readonly ids = new Set<number>();
  constructor(private readonly h: SystemHost) {}

  isElite(e: Entity | undefined): boolean {
    return !!e && this.ids.has(e.id);
  }

  /** Makes `m` an elite (also used by the test). */
  promote(m: Entity): void {
    this.ids.add(m.id);
    m.title = ELITE.tag;
    m.maxHp = Math.round(m.maxHp * ELITE.hp);
    m.hp = m.maxHp;
    m.atk *= ELITE.dmg;
    m.magic *= ELITE.dmg;
  }

  setup(): void {
    const w = this.h.world;
    if (w.map.interior) return;
    const region = regionOf(w.map.zone as ZoneId) ?? 'centre';
    const table = ELITE_TABLE[region];
    for (const m of w.entities.values()) {
      if (m.kind !== 'monster' || !m.brain || !table.includes(m.brain.defId)) continue;
      if (this.h.random() < ELITE.rate) this.promote(m);
    }
  }

  observe(e: WorldEvent): void {
    if (e.type !== 'reward') return;
    const w = this.h.world;
    const m = w.entities.get(e.id);
    if (!m || !this.isElite(m) || !m.brain) return;
    const def = getMonster(m.brain.defId);
    w.progress.gold += def.reward.gold[1] * (ELITE.goldMul - 1);
    w.sys.qiPool.add(def.reward.cultivation * (ELITE.qiMul - 1));
  }
}
